import React, {
  useContext,
  useRef,
  useEffect,
  useState,
  useCallback,
} from 'react'
import { useRelayEnvironment, graphql } from 'react-relay'
import { useField } from 'formik'

import { withStaticProperties, styled, YStack, XStack, View } from 'tamagui'

import { HOST_NAME } from '../../data/config/url'

import { Image } from '../Image'
import { Icon } from '../Icon'
import { Text } from '../Text'
import { Spinner } from '../Spinner'

import { FormFieldContext } from './shared'
import {
  createFormImage,
  FormImageValue,
} from './FormImage'
export type { FormImageValue }

import { FormImageUpload_image$data } from './__generated__/FormImageUpload_image.graphql'

import './FormImageUpload.css'

// Types
type UploadState = 'empty' | 'dragging' | 'loading' | 'preview' | 'error'
type ImageShape = 'default' | 'square' | 'wide'

// Shape dimensions
const SHAPE_STYLES = {
  default: {
    minHeight: 180,
    aspectRatio: undefined,
    width: '100%',
  },
  square: {
    minHeight: undefined,
    aspectRatio: 1,
    width: 180,
    maxWidth: '100%',
  },
  wide: {
    minHeight: undefined,
    aspectRatio: 2,
    width: '100%',
    maxWidth: 400,
  },
} as const

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
const ACCEPTED_EXTENSIONS = '.jpg,.jpeg,.png,.gif,.webp'
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

graphql`
  fragment FormImageUpload_image on Image {
    rowId
    contentType
  }
`

// Styled Components
const DropZone = styled(View, {
  name: 'FormImageUploadDropZone',
  className: 'FormImageUpload',

  borderWidth: 2,
  borderStyle: 'dashed',
  borderColor: '$borderColor',
  borderRadius: '$2',
  backgroundColor: '$backgroundSoft',

  padding: '$4',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  overflow: 'hidden',

  hoverStyle: {
    borderColor: '$borderColorHover',
    backgroundColor: '$backgroundHover',
  },

  focusStyle: {
    borderColor: '$borderColorFocus',
  },

  variants: {
    state: {
      empty: {},
      dragging: {
        className: 'FormImageUpload FormImageUpload_dragging',
      },
      loading: {
        cursor: 'default',
      },
      preview: {
        borderStyle: 'solid',
        padding: '$2',
      },
      error: {
        className: 'FormImageUpload FormImageUpload_error',
        borderColor: '$errorColor',
      },
    },
    shape: {
      default: {
        minHeight: 180,
        width: '100%',
      },
      square: {
        width: 180,
        maxWidth: '100%',
        aspectRatio: 1,
      },
      wide: {
        width: '100%',
        maxWidth: 400,
        aspectRatio: 2,
      },
    },
  } as const,

  defaultVariants: {
    state: 'empty',
    shape: 'default',
  },
} as const)

const PreviewContainer = styled(YStack, {
  name: 'FormImageUploadPreview',
  className: 'FormImageUpload_preview',
  position: 'relative',
  alignItems: 'center',
  gap: '$2',
  width: '100%',
  height: '100%',
})

const PreviewImage = styled(Image, {
  borderRadius: '$2',
  maxHeight: 200,
  maxWidth: '100%',
} as const)

const RemoveButton = styled(View, {
  name: 'FormImageUploadRemoveButton',
  className: 'FormImageUpload_removeButton',
  tag: 'button',

  position: 'absolute',
  top: '$2',
  right: '$2',

  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  borderRadius: '$6',
  padding: '$2',
  borderWidth: 0,

  cursor: 'pointer',
  alignItems: 'center',
  justifyContent: 'center',

  hoverStyle: {
    backgroundColor: '$background',
  },
} as const)

const IconContainer = styled(View, {
  name: 'FormImageUploadIconContainer',
  className: 'FormImageUpload_iconContainer',

  backgroundColor: '$backgroundAccentMuted',
  borderRadius: '$6',
  padding: '$4',
  alignItems: 'center',
  justifyContent: 'center',
} as const)

const FileInfo = styled(XStack, {
  alignItems: 'center',
  gap: '$2',
  paddingTop: '$2',
  flexWrap: 'wrap',
  justifyContent: 'center',
} as const)

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

type FormImageUploadProps = {
  shape?: ImageShape
  recommendedSize?: string
}

export const FormImageUpload = withStaticProperties(
  ({ shape = 'default', recommendedSize }: FormImageUploadProps) => {
    const formFieldContext = useContext(FormFieldContext)
    const isCompact = shape !== 'default'
    const [field, _meta, helpers] = useField<FormImageValue>(
      formFieldContext.name
    )
    const relayEnvironment = useRelayEnvironment()

    const fileInputRef = useRef<HTMLInputElement>(null)
    const dropZoneRef = useRef<HTMLDivElement>(null)

    // State
    const [uploadState, setUploadState] = useState<UploadState>('empty')
    const [fileAsDataURL, setFileAsDataURL] = useState<string | null>(null)
    const [fileName, setFileName] = useState<string | null>(null)
    const [fileSize, setFileSize] = useState<number | null>(null)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    // Determine state based on field value
    useEffect(() => {
      if (field.value?.file) {
        // Don't re-read if we already have this file's preview
        if (fileAsDataURL && fileName === field.value.file.name) {
          setUploadState('preview')
          return
        }

        setUploadState('loading')

        const reader = new FileReader()
        reader.addEventListener('load', (e) => {
          const result = e.target?.result
          if (typeof result === 'string') {
            setFileAsDataURL(result)
            setFileName(field.value.file?.name ?? null)
            setFileSize(field.value.file?.size ?? null)
            setUploadState('preview')
          }
        })
        reader.addEventListener('error', () => {
          setErrorMessage('Failed to read file')
          setUploadState('error')
        })
        reader.readAsDataURL(field.value.file)
      } else if (uploadState !== 'error' && uploadState !== 'dragging') {
        setUploadState('empty')
        setFileAsDataURL(null)
        setFileName(null)
        setFileSize(null)
      }
    }, [field.value])

    // File validation
    const validateFile = useCallback((file: File): string | null => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        return 'Please select a JPEG, PNG, GIF, or WEBP image'
      }
      if (file.size > MAX_FILE_SIZE) {
        return 'File size must be less than 10MB'
      }
      return null
    }, [])

    // Handle file selection
    const handleFileSelect = useCallback(
      (file: File) => {
        setErrorMessage(null)

        const validationError = validateFile(file)
        if (validationError) {
          setErrorMessage(validationError)
          setUploadState('error')
          return
        }

        const formImage = createFormImage(relayEnvironment, file)
        if (formImage) {
          helpers.setValue(formImage)
          helpers.setTouched(true)
        }
      },
      [relayEnvironment, helpers, validateFile]
    )

    // Remove image
    const handleRemove = useCallback(
      (e: any) => {
        e?.stopPropagation?.()
        e?.preventDefault?.()
        helpers.setValue({ uploadImage: async () => undefined, file: undefined })
        setFileAsDataURL(null)
        setFileName(null)
        setFileSize(null)
        setErrorMessage(null)
        setUploadState('empty')
      },
      [helpers]
    )

    // Click to open file picker
    const handleClick = useCallback(() => {
      if (uploadState !== 'loading') {
        fileInputRef.current?.click()
      }
    }, [uploadState])

    // Keyboard handling
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleClick()
        }
      },
      [handleClick]
    )

    // Drag and drop handlers
    const handleDragEnter = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (uploadState !== 'loading') {
          setUploadState('dragging')
        }
      },
      [uploadState]
    )

    const handleDragLeave = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        // Only set to empty if leaving the drop zone entirely
        if (!dropZoneRef.current?.contains(e.relatedTarget as Node)) {
          setUploadState(field.value?.file ? 'preview' : 'empty')
        }
      },
      [field.value]
    )

    const handleDragOver = useCallback((e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
    }, [])

    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()

        setUploadState('empty')

        const files = e.dataTransfer.files
        if (files.length > 0) {
          handleFileSelect(files[0])
        }
      },
      [handleFileSelect]
    )

    // File input change
    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files
        if (files && files.length > 0) {
          handleFileSelect(files[0])
        }
        // Reset input so same file can be selected again
        e.target.value = ''
      },
      [handleFileSelect]
    )

    return (
      <DropZone
        ref={dropZoneRef}
        state={uploadState}
        shape={shape}
        onPress={handleClick}
        // @ts-expect-error onKeyDown is valid on web
        onKeyDown={handleKeyDown}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        tabIndex={0}
        role='button'
        aria-label='Upload image'
      >
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type='file'
          accept={ACCEPTED_EXTENSIONS}
          onChange={handleInputChange}
          className='FormImageUpload_hiddenInput'
          tabIndex={-1}
          aria-hidden='true'
        />

        {/* Empty state */}
        {(uploadState === 'empty' || uploadState === 'dragging') && (
          <YStack alignItems='center' gap={isCompact ? '$2' : '$3'}>
            <IconContainer padding={isCompact ? '$2' : '$4'}>
              <Icon
                name='arrow-circle-up'
                size={isCompact ? '$5' : '$8'}
                color={uploadState === 'dragging' ? '$primaryColor' : '$colorSubtle'}
              />
            </IconContainer>

            <YStack alignItems='center' gap='$1'>
              <Text
                fontSize={isCompact ? '$3' : '$5'}
                fontWeight='$5'
                color='$color'
                textAlign='center'
              >
                {uploadState === 'dragging'
                  ? 'Drop here'
                  : isCompact
                    ? 'Drop or click'
                    : 'Drop image here or click to upload'}
              </Text>
              {recommendedSize && (
                <Text fontSize='$3' color='$colorSubtle' textAlign='center'>
                  {recommendedSize}
                </Text>
              )}
              {!isCompact && !recommendedSize && (
                <Text fontSize='$4' color='$colorSubtle'>
                  JPEG, PNG, GIF, or WEBP (max 10MB)
                </Text>
              )}
            </YStack>
          </YStack>
        )}

        {/* Loading state */}
        {uploadState === 'loading' && (
          <YStack alignItems='center' gap={isCompact ? '$2' : '$3'}>
            <Spinner size={isCompact ? '$5' : '$8'} color='$primaryColor' />
            {!isCompact && (
              <Text fontSize='$4' color='$colorMuted'>
                Processing image...
              </Text>
            )}
          </YStack>
        )}

        {/* Preview state */}
        {uploadState === 'preview' && fileAsDataURL && (
          <PreviewContainer>
            <View position='relative' width='100%' height='100%'>
              <PreviewImage
                src={fileAsDataURL}
                alt='Upload preview'
                width='100%'
                height='100%'
                objectFit='cover'
                maxHeight={isCompact ? undefined : 200}
              />

              <RemoveButton
                onPress={handleRemove}
                aria-label='Remove image'
                top={isCompact ? '$1' : '$2'}
                right={isCompact ? '$1' : '$2'}
                padding={isCompact ? '$1' : '$2'}
              >
                <Icon name='dismiss' size={isCompact ? '$3' : '$4'} color='$errorColor' />
              </RemoveButton>
            </View>

            {!isCompact && (fileName || fileSize) && (
              <FileInfo>
                {fileName && (
                  <Text
                    fontSize='$3'
                    color='$colorMuted'
                    numberOfLines={1}
                    maxWidth={200}
                  >
                    {fileName}
                  </Text>
                )}
                {fileSize && (
                  <Text fontSize='$3' color='$colorSubtle'>
                    ({formatFileSize(fileSize)})
                  </Text>
                )}
              </FileInfo>
            )}

            {!isCompact && (
              <Text fontSize='$3' color='$primaryColor' fontWeight='$5'>
                Click to replace
              </Text>
            )}
          </PreviewContainer>
        )}

        {/* Error state */}
        {uploadState === 'error' && (
          <YStack alignItems='center' gap={isCompact ? '$2' : '$3'}>
            <IconContainer backgroundColor='$red2' padding={isCompact ? '$2' : '$4'}>
              <Icon name='error-circle' size={isCompact ? '$5' : '$8'} color='$errorColor' />
            </IconContainer>

            <YStack alignItems='center' gap='$1'>
              <Text
                fontSize={isCompact ? '$3' : '$5'}
                fontWeight='$5'
                color='$errorColor'
                textAlign='center'
              >
                {errorMessage || 'Error'}
              </Text>
              <Text fontSize={isCompact ? '$2' : '$4'} color='$colorSubtle'>
                {isCompact ? 'Try again' : 'Click to try again'}
              </Text>
            </YStack>
          </YStack>
        )}
      </DropZone>
    )
  },
  {
    prepareInitialValue: async (
      image: Omit<FormImageUpload_image$data, ' $fragmentType'> | null | undefined
    ): Promise<FormImageValue> => {
      if (!image) {
        return { uploadImage: async () => undefined, file: undefined }
      }

      const fileName = `${image.rowId}.${image.contentType.toLowerCase()}`

      // Fetch original image from Cloudflare R2 via image worker
      const response = await fetch(
        `${HOST_NAME}/i/${image.rowId}/${image.contentType.toLowerCase()}`
      )

      const arrayBuffer = await response.arrayBuffer()

      const file = new File([arrayBuffer], fileName)

      return { uploadImage: async () => undefined, file }
    },
  }
)
